import React, { useMemo, useState } from "react";
import {
  Exercise,
  ExerciseSet,
  CreateWorkoutRequest,
  WorkoutDetails,
  PatchWorkoutRequest,
  ExerciseDetails,
} from "../../api/types.ts";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { Dropdown } from "../widgets/Dropdown.tsx";
import { DropdownItem } from "../widgets/DropdownItem.tsx";
import { InlineTextInput } from "../inputs/InlineTextInput.tsx";
import { MultiInlineEdit } from "../inputs/MultiInlineEdit.tsx";
import { InlineDatePicker } from "../inputs/InlineDatePicker.tsx";
import {
  useCreateWorkout,
  usePatchWorkout,
  useDeleteWorkout,
  useCompleteWorkout,
} from "../../api/hooks.ts";
import { InlineTimePicker } from "../inputs/InlineTimePicker.tsx";
import { WorkoutExerciseList } from "./WorkoutExerciseList.tsx";
import { useToast } from "../ToastContext.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";
import { FaCircle } from "react-icons/fa";
import { formatDate } from "../../util/date.ts";
import { Card } from "../containers/Card.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";
import { useDialog } from "../DialogManager.tsx";
import { InlineTextArea } from "../inputs/InlineTextArea.tsx";

interface WorkoutCardProps {
  workout: WorkoutDetails;
  exercises: Map<string, Exercise>;
  timeFormat: "12h" | "24h";
}

const WorkoutCard: React.FC<WorkoutCardProps> = ({
  workout,
  exercises,
  timeFormat,
}) => {
  const { openDialog, setError } = useDialog();
  const { showInfo } = useToast();

  const availableExerciseSnapshots = useMemo(() => {
    const available = new Map<string, ExerciseDetails>();

    workout.exercises.forEach((ex) => available.set(ex.snapshot_id!, ex));

    return available;
  }, [workout]);

  const availableExercises = useMemo(() => {
    const available = new Map<string, FetchedExercise>();

    exercises.forEach((ex) =>
      available.set(ex.id, { exercise: ex, external: false }),
    );

    return available;
  }, [exercises]);

  const patchWorkoutMutation = usePatchWorkout();
  const deleteWorkoutMutation = useDeleteWorkout();
  const completeWorkoutMutation = useCompleteWorkout();
  const createWorkoutMutation = useCreateWorkout();

  const [tempWorkoutName, setTempWorkoutName] = useState(workout.name);
  const [tempWorkoutDate, setTempWorkoutDate] = useState<Date | null>(
    workout.executed_at ? new Date(workout.executed_at) : null,
  );
  const [isFocused, setIsFocused] = useState(false);

  const [tempDescription, setTempDescription] = useState<string | undefined>(
    workout.description,
  );

  const handleEdit = (workout: PatchWorkoutRequest) => {
    patchWorkoutMutation.mutate({ id: workout.id, workout });
  };

  const handleDelete = (workout: WorkoutDetails) => {
    deleteWorkoutMutation.mutate(workout.id, {
      onError: (e) => {
        setError(e);
      },
    });
  };

  const handleDeleteClick = (workout: WorkoutDetails) => {
    openDialog("confirm", {
      onConfirm: () => handleDelete(workout),
      title: "Delete Workout",
      message: `Are you sure you want to delete "${workout.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });
  };

  const handleWorkoutDetailsCancel = () => {
    setTempWorkoutName(workout.name);
    setTempWorkoutDate(
      workout.executed_at ? new Date(workout.executed_at) : null,
    );
  };

  function moveWorkoutExercise(
    workoutExercises: ExerciseDetails[],
    oldIndex: number,
    newIndex: number,
  ) {
    const removed = workoutExercises.splice(oldIndex, 1)[0];
    if (newIndex === 0) {
      workoutExercises.unshift(removed);
    } else {
      workoutExercises.splice(newIndex, 0, removed);
    }
  }

  const handleWorkoutExerciseChange = (
    workoutExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId?: string,
    newSnapshotId?: string,
    newWorkoutExerciseIndex?: number,
  ) => {
    const updatedWorkoutExercises = workout.exercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        return {
          ...ex,
          exercise_id: newExerciseId,
          snapshot_id: newSnapshotId,
          resistance_unit: newUnit,
        };
      }
      return ex;
    });
    if (isNotNullish(newWorkoutExerciseIndex)) {
      moveWorkoutExercise(
        updatedWorkoutExercises,
        workoutExerciseIndex,
        newWorkoutExerciseIndex,
      );
    }
    handleEdit({ ...workout, exercises: updatedWorkoutExercises });
  };

  const handleDuplicateWorkout = () => {
    const duplicatedWorkout: CreateWorkoutRequest = {
      ...workout,
      name: `${workout.name} (Copy)`,
    };
    createWorkoutMutation.mutate(duplicatedWorkout, {
      onSuccess: () => {
        showInfo(`Duplicated ${workout.name}`);
      },
    });
  };

  const handleDeleteWorkoutExercise = (workoutExerciseIndex: number) => {
    const updatedWorkoutExercises = workout.exercises.filter(
      (_, idx) => idx !== workoutExerciseIndex,
    );
    handleEdit({ ...workout, exercises: updatedWorkoutExercises });
  };

  const handleAddWorkoutExercise = (exerciseId: string) => {
    const exercise = exercises.get(exerciseId);
    if (!exercise) return;
    const updatedWorkoutExercises = [
      ...workout.exercises,
      {
        exercise_id: exerciseId,
        sets: [] as ExerciseSet[],
        resistance_unit: exercise.default_resistance_unit,
      },
    ];
    handleEdit({ ...workout, exercises: updatedWorkoutExercises });
  };

  const handleAddSet = (
    workoutExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedWorkoutExercises = workout.exercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        return {
          ...ex,
          sets: [...ex.sets, { repetitions, resistance }],
        };
      }
      return ex;
    });
    handleEdit({ ...workout, exercises: updatedWorkoutExercises });
  };

  const handleEditSet = (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedWorkoutExercises = workout.exercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        // Delete set if both repetitions and resistance are zero
        if (repetitions === 0 && resistance === 0) {
          const updatedSets = ex.sets.filter((_, index) => index !== setIndex);
          return {
            ...ex,
            sets: updatedSets,
          };
        }

        // Otherwise, update the set
        const updatedSets = ex.sets.map((set, index) => {
          if (index === setIndex) {
            return { repetitions, resistance };
          }
          return set;
        });
        return {
          ...ex,
          sets: updatedSets,
        };
      }
      return ex;
    });
    handleEdit({ ...workout, exercises: updatedWorkoutExercises });
  };

  return (
    <>
      <Card>
        <Card.Header>
          <div className="w-full flex flex-col">
            {workout.status === "active" && (
              <div className="flex flex-row gap-0.5 text-success h-fit items-center mx-2">
                <FaCircle className="p-1 animate-pulse -ml-2" />
                <span className="text-sm">In Progress</span>
              </div>
            )}
            <div className="w-full flex justify-between items-center">
              <div className="w-full" onClick={(e) => e.stopPropagation()}>
                <MultiInlineEdit
                  displayValue={workout.name}
                  className="flex flex-wrap gap-2 items-center"
                  displayClassName="font-semibold text-lg"
                  onSave={() => {
                    handleEdit({
                      ...workout,
                      name: tempWorkoutName,
                      executed_at: tempWorkoutDate
                        ? tempWorkoutDate.toISOString()
                        : workout.executed_at,
                    });
                  }}
                  onCancel={handleWorkoutDetailsCancel}
                  displayAs="h4"
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                >
                  <InlineTextInput
                    value={tempWorkoutName}
                    onChange={setTempWorkoutName}
                    placeholder="Workout name"
                    className="max-w-32 sm:max-w-64"
                    isSimple
                  />
                  <InlineDatePicker
                    value={tempWorkoutDate}
                    onChange={setTempWorkoutDate}
                    placeholder="Select date"
                    displayClassName="text-sm"
                    isSimple
                  />
                  <InlineTimePicker
                    value={tempWorkoutDate}
                    onChange={setTempWorkoutDate}
                    displayClassName="text-sm"
                    format={timeFormat}
                    isSimple
                  />
                </MultiInlineEdit>
                {!isFocused && (
                  <div className="flex flex-col gap-2 pb-2">
                    <span className="text-sm mx-2">
                      {formatDate(new Date(workout.executed_at), timeFormat)}
                    </span>
                  </div>
                )}
              </div>
              <Dropdown
                trigger={
                  <button
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Workout options"
                  >
                    <HiOutlineDotsVertical
                      className="text-gray-600"
                      size={20}
                    />
                  </button>
                }
                alignment="left"
              >
                {workout.status === "active" && (
                  <DropdownItem
                    onClick={() => completeWorkoutMutation.mutate(workout.id)}
                  >
                    Mark as Completed
                  </DropdownItem>
                )}
                {/* <DropdownItem
                    onClick={() =>
                      openDialog("edit-workout", {
                        workout: workout,
                        exercises: Array.from(exercises).map((ex) => ex[1]),
                      })
                    }
                  >
                    Edit
                  </DropdownItem> */}

                <DropdownItem onClick={handleDuplicateWorkout}>
                  Duplicate
                </DropdownItem>

                <DropdownItem
                  onClick={() => handleDeleteClick(workout)}
                  className="text-red-600 hover:bg-red-50"
                >
                  Delete
                </DropdownItem>
              </Dropdown>
            </div>
            <InlineTextArea
              value={tempDescription ?? ""}
              onChange={setTempDescription}
              onSave={() => {
                handleEdit({
                  ...workout,
                  description: tempDescription,
                });
              }}
              onCancel={() => setTempDescription(workout.description)}
              placeholder="How did it go?"
              displayClassName="text-gray-600 text-sm"
            />
          </div>
        </Card.Header>
        <Card.Content>
          <WorkoutExerciseList
            resourceId={workout.id}
            workoutExercises={workout.exercises}
            availableExercises={availableExercises}
            availableExerciseSnapshots={availableExerciseSnapshots}
            onExerciseChange={handleWorkoutExerciseChange}
            onDeleteExercise={handleDeleteWorkoutExercise}
            onAddExercise={handleAddWorkoutExercise}
            onAddSet={handleAddSet}
            onEditSet={handleEditSet}
          />
        </Card.Content>
        <Card.Footer>
          <div className="flex flex-col">
            {/* Footer stats */}
            <span className="text-sm text-gray-600">
              <span className="font-medium">{workout.exercises.length}</span>{" "}
              exercise
              {workout.exercises.length !== 1 ? "s" : ""}
            </span>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export { WorkoutCard };
